<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEvents } from '@/composables/useEvents'
import { useEditions } from '@/composables/useEditions'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import EditionSchedulePanel from '@/pages/organizer/events/editions/settings/EditionSchedulePanel.vue'

const { t } = useI18n()
const route = useRoute()

const eventSlug = route.params['eventSlug'] as string
const editionSlug = route.params['editionSlug'] as string

const { event, fetchEvent } = useEvents()
const { edition, fetchEdition } = useEditions()

const pageLoading = ref(true)

onMounted(async () => {
  await Promise.all([fetchEvent(eventSlug), fetchEdition(eventSlug, editionSlug)])
  pageLoading.value = false
})
</script>

<template>
  <OrganizerLayout>
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Breadcrumb -->
      <nav aria-label="breadcrumb" class="flex items-center gap-2 text-xs text-slate-500 mb-6 flex-wrap">
        <RouterLink to="/organizer/dashboard" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.dashboard') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink to="/organizer/events" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.events') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}`" class="hover:text-slate-300 transition-colors">
          <template v-if="event">{{ event.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink :to="`/organizer/events/${eventSlug}/editions/${editionSlug}`" class="hover:text-slate-300 transition-colors">
          <template v-if="edition">{{ edition.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </RouterLink>
        <span aria-hidden="true">/</span>
        <span class="text-slate-300">{{ t('nav.sidebar.programme') }}</span>
      </nav>

      <div class="mb-8">
        <h1 class="text-3xl font-bold text-white">
          {{ event?.name ?? '…' }} · {{ edition?.name ?? '…' }}
        </h1>
        <p class="text-slate-400 mt-1">{{ t('nav.sidebar.programme') }}</p>
      </div>

      <!-- Loading -->
      <AppSpinner v-if="pageLoading" />

      <EditionSchedulePanel v-else />
    </main>
  </OrganizerLayout>
</template>
